import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/ui/button';
import { Camera, X, MapPin, ArrowCounterClockwise } from '@phosphor-icons/react';

/**
 * Live camera capture (getUserMedia + <video>).
 *
 * Hardening:
 *  - Rear camera preferred (`facingMode: environment`) with graceful fallback
 *    to any available camera when rear isn't exposed.
 *  - Shutter button is ALWAYS enabled. On tap, if the video element has a
 *    valid frame (`readyState >= 2`) we capture immediately. Otherwise we
 *    attempt `video.play()` inside the user gesture (which is allowed under
 *    Android autoplay policy) and retry the capture once.
 *  - Stream is stopped on unmount so the camera LED turns off.
 *  - Gallery upload is intentionally removed — pilots must take the photo
 *    live at the pickup / drop location.
 *  - The component is portaled into document.body so it sits above any
 *    parent Radix Dialog without fighting its focus trap.
 */
export default function CameraCapture({ onCapture, onClose, label = 'Take Photo' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch {}
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraReady(false);
    setCameraError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera API not supported on this browser.');
      return;
    }
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch {
        // Fallback: no facing constraint at all
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;
        try { await videoRef.current.play(); } catch {}
      }
    } catch (err) {
      setCameraError(
        err && err.name === 'NotAllowedError'
          ? 'Camera permission denied. Enable it in browser settings and reload.'
          : 'Could not start camera. Please check permissions and try again.'
      );
    }
  }, []);

  const getGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsError('');
      },
      (err) => setGpsError('GPS unavailable: ' + err.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  useEffect(() => { startCamera(); getGPS(); return () => stopStream(); }, [startCamera, getGPS, stopStream]);

  const captureFromVideo = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return false;
    if (!v.videoWidth || !v.videoHeight) return false;
    // Phase 78aq — auto-resize the captured frame before JPEG-encoding.
    // The raw camera frame can be up to 1920x1080 (~500-800 KB JPEG)
    // which is far more than needed for KM-reading / DC verification and
    // slows pilot uploads on 4G. We clamp the longer edge to 1280px
    // (kept proportional) and re-encode at quality 0.72 → typical
    // output ~120-200 KB while remaining sharp enough for OCR.
    const MAX_EDGE = 1280;
    const JPEG_QUALITY = 0.72;
    let outW = v.videoWidth;
    let outH = v.videoHeight;
    const longEdge = Math.max(outW, outH);
    if (longEdge > MAX_EDGE) {
      const scale = MAX_EDGE / longEdge;
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }
    c.width = outW;
    c.height = outH;
    const ctx = c.getContext('2d');
    // Smooth scaling for the downsample.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(v, 0, 0, outW, outH);
    const dataUrl = c.toDataURL('image/jpeg', JPEG_QUALITY);
    setPhoto(dataUrl);
    return true;
  };

  const handleShutter = async () => {
    if (capturing) return;
    setCapturing(true);
    try {
      // Try immediately — if video already has a frame, this works.
      if (captureFromVideo()) return;
      // Otherwise attempt to (re)start playback inside the user-gesture
      // context, then retry once after a short delay.
      if (videoRef.current) {
        try { await videoRef.current.play(); } catch {}
      }
      await new Promise(r => setTimeout(r, 300));
      if (!captureFromVideo()) {
        setCameraError('Camera not ready yet. Please wait a moment and tap again.');
      }
    } finally {
      setCapturing(false);
    }
  };

  const retake = () => {
    setPhoto(null);
    // Video stream is still running — nothing else to do.
  };

  const usePhoto = () => {
    if (onCapture) onCapture({ photo, gps, timestamp: new Date().toISOString() });
    stopStream();
    if (onClose) onClose();
  };

  const handleClose = () => {
    stopStream();
    if (onClose) onClose();
  };

  // Fire cameraReady once we have video dimensions via any of several events.
  const markReady = () => setCameraReady(true);

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="camera-capture">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/90">
        <span className="text-white text-sm font-medium uppercase tracking-widest">{label}</span>
        <button onClick={handleClose} className="text-white/60 hover:text-white" data-testid="camera-close-btn">
          <X size={24} />
        </button>
      </div>

      {/* Preview area */}
      <div className="flex-1 relative flex items-center justify-center bg-black overflow-hidden">
        {photo ? (
          <img src={photo} alt="Captured" className="max-h-full max-w-full object-contain" />
        ) : (
          <>
            <video
              ref={videoRef}
              className="max-h-full max-w-full object-contain"
              autoPlay
              playsInline
              muted
              onLoadedMetadata={markReady}
              onLoadedData={markReady}
              onCanPlay={markReady}
              onPlaying={markReady}
              data-testid="camera-video"
            />
            {!cameraReady && !cameraError && (
              <div className="absolute inset-0 flex items-center justify-center text-white/60 text-xs uppercase tracking-widest animate-pulse">
                Starting camera…
              </div>
            )}
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center gap-3">
                <Camera size={56} className="text-white/40" weight="duotone" />
                <p className="text-[#FF3333] text-sm font-medium max-w-xs">{cameraError}</p>
                <button
                  onClick={startCamera}
                  className="text-xs uppercase tracking-widest text-white/80 underline"
                  data-testid="camera-retry-btn"
                >
                  Retry
                </button>
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* GPS Info */}
      <div className="px-4 py-2 bg-black/90 flex items-center gap-2">
        <MapPin size={14} className={gps ? 'text-[#00C853]' : 'text-[#FF3333]'} />
        <span className="text-xs text-white/60">
          {gps ? `${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)} (${Math.round(gps.accuracy)}m)` : gpsError || 'Acquiring GPS…'}
        </span>
      </div>

      {/* Controls — Phase 78aq. `paddingBottom` uses the iOS/Android
          safe-area-inset (gesture nav bar) plus a 24px floor so the
          Retake / Use Photo buttons are never hidden under the system
          nav. Also `pt-3 pb-4 px-4` baseline keeps the layout breathing
          on edge-to-edge devices. */}
      <div
        className="bg-black/90 flex items-center justify-center gap-4 pt-3 px-4"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}
      >
        {!photo ? (
          <button
            type="button"
            onClick={handleShutter}
            disabled={capturing || !!cameraError}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-transform active:scale-90 select-none ${capturing ? 'bg-[#002FA7] text-white' : 'bg-white hover:bg-white/90 shadow-lg'} disabled:opacity-40`}
            style={{ WebkitTapHighlightColor: 'rgba(0,47,167,0.3)', touchAction: 'manipulation' }}
            data-testid="camera-capture-btn"
            aria-label="Take photo"
          >
            <Camera size={32} className={capturing ? 'text-white animate-pulse' : 'text-[#0A0A0A]'} weight="bold" />
          </button>
        ) : (
          <>
            <Button onClick={retake} className="bg-white/10 text-white hover:bg-white/20 hover:text-white rounded-none" data-testid="camera-retake-btn">
              <ArrowCounterClockwise size={16} className="mr-2" /> Retake
            </Button>
            <Button onClick={usePhoto} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="camera-use-btn">
              Use Photo
            </Button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
